import { Button } from '@avanta/shared/components/ui/Button';
import { Dialog } from '@avanta/shared/components/ui/Dialog';
import { Textarea } from '@avanta/shared/components/ui/Textarea';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { useIsMutating } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { MUTATION_KEYS } from 'constants/common';
import { INITIAL_STEP_INDEX } from 'pages/Appointments/AppointmentsReserve/hooks/useStepNavigation';
import {
  AppointmentsReservePayload,
  type StepProps,
} from 'pages/Appointments/AppointmentsReserve/types';
import { FLOW_MODES } from 'types/appointments';
import { BackButton } from '../../../ui/BackButton';
import { PsychoCard } from '../../../ui/PsychoCard';
import { SessionSummary } from '../../../ui/SessionSummary';
import { StepSection } from '../../../ui/StepSection';
import { Pack } from './Pack';
import { CONFIRMATION_STEPS_KEY_PREFIX } from '.';

export const DesktopView = ({ handleBack }: StepProps): React.ReactElement => {
  const { watch } = useFormContext();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { showDialog, hideDialog } = useDialogContext();
  const isPendingEdit = useIsMutating({ mutationKey: [MUTATION_KEYS.UPDATE_APPOINTMENT] }) > 0;
  const isPendingBook = useIsMutating({ mutationKey: [MUTATION_KEYS.BOOK_APPOINTMENT] }) > 0;
  const isPending = isPendingEdit || isPendingBook;

  const [psychologist, selectedPack, flow] = watch([
    AppointmentsReservePayload.psychologist,
    AppointmentsReservePayload.selectedPack,
    AppointmentsReservePayload.flow,
  ]);

  const commentsLabel = (
    <Trans
      ns={LOCIZE_NS.APPOINTMENTS}
      i18nKey={`${CONFIRMATION_STEPS_KEY_PREFIX}commentsLabel`}
      components={{
        span: <span className="text-gray-400 text-sm font-['Inter'] font-normal" />,
      }}
    />
  );

  const handleReset = (): void => {
    handleBack(INITIAL_STEP_INDEX);
  };

  const handleOnBack = (): void => {
    if (selectedPack) {
      showDialog({
        component: (
          <Dialog
            title={t(`${CONFIRMATION_STEPS_KEY_PREFIX}dialog.reset.title`)}
            description={t(`${CONFIRMATION_STEPS_KEY_PREFIX}dialog.reset.description`)}
            open
            primaryAction={{
              label: t(`${CONFIRMATION_STEPS_KEY_PREFIX}backLabel.reset`),
              action: handleReset,
            }}
            secondaryAction={{
              label: t(`${LOCIZE_NS.COMMON}:Common.back`),
              action: hideDialog,
            }}
          />
        ),
      });
    } else {
      handleBack();
    }
  };

  return (
    <div className="hidden am-lg:flex flex-col gap-2">
      <h4 className="font-medium leading-5 text-denim-800">
        {t(`${CONFIRMATION_STEPS_KEY_PREFIX}resumeLabel.regular`)}
      </h4>
      <div className="flex gap-4 flex-1 min-h-0">
        <SessionSummary className="justify-between" showLanguages={flow === FLOW_MODES.regular} />
        <div className="flex flex-col gap-10 overflow-y-auto scrollbar-custom">
          <StepSection className="flex flex-col gap-4">
            <PsychoCard psychologist={psychologist} showReadMoreButton={false} />
            {selectedPack && <Pack />}
          </StepSection>
          <div className="flex justify-between gap-4">
            <BackButton
              handleBack={handleOnBack}
              label={
                selectedPack ? t(`${CONFIRMATION_STEPS_KEY_PREFIX}backLabel.reset`) : undefined
              }
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-43"
              isLoading={isPending}
            >
              {t(`${CONFIRMATION_STEPS_KEY_PREFIX}onSubmit`)}
            </Button>
          </div>
          <StepSection
            title={commentsLabel}
            className="flex flex-col gap-2"
            containerClassName="gap-2 mt-6"
          >
            <p className="text-sm text-denim-800 leading-4">
              {t(`${CONFIRMATION_STEPS_KEY_PREFIX}commentsDescription`)}
            </p>
            <Textarea
              placeholder={t(`${CONFIRMATION_STEPS_KEY_PREFIX}observations.placeholder`)}
              className="min-h-14"
            />
          </StepSection>
        </div>
      </div>
    </div>
  );
};
